/**
 * Shared template expansion logic — single source of truth.
 *
 * Pure JS so it can be imported by both TypeScript (runtime, preview)
 * and plain JS (property-bake.js) without a TS loader.
 *
 * Takes an array of resolved pages and a propertiesByState index,
 * returns an expanded array with template pages instantiated.
 */

/**
 * Replace {{field_name}} tokens in a string with values from a tokens map.
 * @param {string|undefined} template
 * @param {Record<string, string>} tokens
 * @returns {string}
 */
function resolveTokens(template, tokens) {
  if (!template) return '';
  return template.replace(/\{\{(\w+)\}\}/g, (_, field) => tokens[field] ?? '');
}

/**
 * Create a concrete page from a template by resolving all token placeholders.
 * Expanded pages are always visible (the template's `visible: false` only hides
 * the template definition itself from the static page list / navigation).
 */
function instantiateTemplate(template, tokens, alias) {
  return {
    ...template,
    alias,
    visible: true,
    name: resolveTokens(template.name, tokens),
    path: resolveTokens(template.path, tokens),
    seo_title: template.seo_title ? resolveTokens(template.seo_title, tokens) : undefined,
    seo_description: template.seo_description ? resolveTokens(template.seo_description, tokens) : undefined,
  };
}

function buildStateTokens(state) {
  return {
    state_name: state.name,
    state_abbr: state.abbr,
    state_slug: state.slug,
  };
}

function buildCityTokens(state, city) {
  return {
    ...buildStateTokens(state),
    city_name: city.name,
    city_slug: city.slug,
  };
}

function buildPropertyTokens(state, city, prop) {
  // Expose every scalar property field as a token, matching resolveTemplate's
  // any-property-field contract (page-utils.ts) — template pages reference
  // fields like {{property_seo_description_floorplans}}, not just the
  // name/address basics. Nested objects/arrays are not addressable tokens.
  const tokens = {};
  for (const [key, value] of Object.entries(prop)) {
    if (typeof value === 'string' || typeof value === 'number') tokens[key] = String(value);
  }
  // State/city tokens and the normalized slug win any collision with raw
  // property fields, preserving the pre-existing token values.
  Object.assign(tokens, buildCityTokens(state, city));
  tokens.property_slug = prop._slug || '';
  return tokens;
}

function buildHyperlocalTokens(state, city, prop, hl) {
  return {
    ...buildPropertyTokens(state, city, prop),
    page_item_url: hl.page_item_url || '',
    h1_title: hl.h1_title || '',
    h2_subtitle: hl.h2_subtitle || '',
    search_query: hl.search_query || '',
    category: hl.category || '',
    seo_title: hl.seo_title || '',
    seo_description: hl.seo_description || '',
  };
}

/**
 * Expand template pages into concrete pages using a property index.
 *
 * Template pages use `{{token}}` placeholders in their `path`, `name`,
 * `seo_title`, etc. Static (non-template) pages pass through unchanged.
 *
 * @param {Array<object>} resolvedPages - Output of mergePages()
 * @param {Record<string, object>} propertiesByState - Nested state→city→properties index
 * @returns {Array<object>} Expanded array of resolved pages with unique aliases.
 */
export function expandTemplatePages(resolvedPages, propertiesByState) {
  const expanded = [];

  for (const page of resolvedPages) {
    // deleted_at unpublishes the template and every instance it would generate
    if (page.deleted_at) continue;
    if (!page.template) {
      expanded.push(page);
      continue;
    }

    switch (page.template) {
      case 'state':
        for (const state of Object.values(propertiesByState)) {
          const tokens = buildStateTokens(state);
          expanded.push(instantiateTemplate(page, tokens, `${page.alias}--${state.slug}`));
        }
        break;

      case 'city':
        for (const state of Object.values(propertiesByState)) {
          for (const city of Object.values(state.cities)) {
            const tokens = buildCityTokens(state, city);
            expanded.push(instantiateTemplate(page, tokens, `${page.alias}--${state.slug}--${city.slug}`));
          }
        }
        break;

      case 'property':
        for (const state of Object.values(propertiesByState)) {
          for (const city of Object.values(state.cities)) {
            for (const prop of city.properties) {
              const tokens = buildPropertyTokens(state, city, prop);
              const alias = `${page.alias}--${state.slug}--${city.slug}--${prop._slug}`;
              expanded.push(instantiateTemplate(page, tokens, alias));
            }
          }
        }
        break;

      case 'hyperlocal':
        for (const state of Object.values(propertiesByState)) {
          for (const city of Object.values(state.cities)) {
            for (const prop of city.properties) {
              const hyperlocals = prop.hyperlocal_queries || [];
              for (const hl of hyperlocals) {
                if (!hl.page_item_url) continue;
                const tokens = buildHyperlocalTokens(state, city, prop, hl);
                const alias = `${page.alias}--${state.slug}--${city.slug}--${prop._slug}--${hl.page_item_url}`;
                expanded.push(instantiateTemplate(page, tokens, alias));
              }
            }
          }
        }
        break;
    }
  }

  return expanded;
}
